import { Controller, useFormContext, type FieldValues, type Path } from "react-hook-form";
import type { HTMLInputTypeAttribute, ReactNode } from "react";
import { Field } from "@/components/field";

/**
 * Form-aware Field — bridges react-hook-form's Controller to our brand
 * `<Field>` wrapper (MUI OutlinedInput underneath). Pulls validation errors
 * from RHF's fieldState and surfaces them via Field's `error` prop.
 *
 * The `name` prop is constrained to the parent Form's schema via the
 * generic. Inside a `<Form schema={loginSchema}>`, `name` autocompletes to
 * the schema's keys.
 *
 * Pass any visual prop the underlying Field accepts: `label`, `hint`,
 * `placeholder`, `leftIcon`, `rightIcon`, `unit`, `type`, `autoComplete`,
 * etc. Validation comes from the schema; messages flow through automatically.
 */
type FormFieldProps<F extends FieldValues> = {
  name: Path<F>;
  label?: string;
  hint?: string;
  placeholder?: string;
  type?: HTMLInputTypeAttribute;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  unit?: string;
  autoComplete?: string;
  disabled?: boolean;
  className?: string;
};

export function FormField<F extends FieldValues>({
  name,
  label,
  hint,
  placeholder,
  type = "text",
  leftIcon,
  rightIcon,
  unit,
  autoComplete,
  disabled,
  className,
}: FormFieldProps<F>) {
  const { control } = useFormContext<F>();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => (
        <Field
          {...field}
          value={field.value ?? ""}
          label={label}
          hint={hint}
          placeholder={placeholder}
          type={type}
          leftIcon={leftIcon}
          rightIcon={rightIcon}
          unit={unit}
          autoComplete={autoComplete}
          disabled={disabled}
          className={className}
          error={fieldState.error?.message}
          inputRef={field.ref}
        />
      )}
    />
  );
}
